import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, MapPin, Clock, FileText, CheckCircle, ChevronDown, ChevronUp,
  Loader2, AlertCircle, Send, Check, MessageSquare, RefreshCw, User
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import { contractorJobService } from '../../../services/contractorJobService';
import { overtimeService } from '../../../services/overtimeService';
import PhotoUploader from './PhotoUploader';
import OvertimeRequestModal from '../OvertimeRequestModal';

const STORAGE_KEY_PREFIX = 'job_report_draft_';

/**
 * JobReportDialog - Mobile-optimized job report submission dialog
 *
 * A floating dialog that displays all report sections in a single scrollable form.
 * Supports auto-save, data persistence, and can be dismissed at any time.
 */
export default function JobReportDialog({ instanceId, onClose }) {
  const navigate = useNavigate();

  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Job data
  const [jobInstance, setJobInstance] = useState(null);

  // Form state
  const [workNotes, setWorkNotes] = useState('');
  const [checklist, setChecklist] = useState([]);
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [notesPhotos, setNotesPhotos] = useState([]);
  const [questionnaire, setQuestionnaire] = useState([]);

  // UI state
  const [showOverview, setShowOverview] = useState(false);
  const [clockedIn, setClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);

  // Overtime state
  const [showOvertimeModal, setShowOvertimeModal] = useState(false);
  const [overtimeInfo, setOvertimeInfo] = useState(null);
  const [timesheetId, setTimesheetId] = useState(null);

  // Requirements from job
  const [requirements, setRequirements] = useState({
    photosRequiredBefore: false,
    photosRequiredAfter: false,
    questionnaireTemplate: [],
    clockInFlexibility: false
  });

  // Manual time entry (for late clock-in)
  const [manualClockIn, setManualClockIn] = useState('');
  const [manualClockOut, setManualClockOut] = useState('');

  // Revision/dispute state (for resubmission flow)
  const [isResubmission, setIsResubmission] = useState(false);
  const [revisionFeedback, setRevisionFeedback] = useState(null);

  // Editable times for resubmission
  const [editClockIn, setEditClockIn] = useState('');
  const [editClockOut, setEditClockOut] = useState('');

  // Auto-save timer ref
  const autoSaveTimerRef = useRef(null);
  const lastSavedRef = useRef(null);

  // Load job instance data
  useEffect(() => {
    if (instanceId) {
      loadJobInstance();
    }
  }, [instanceId]);

  // Auto-save on changes (debounced)
  useEffect(() => {
    if (!jobInstance || !clockedIn) return;

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer for auto-save
    autoSaveTimerRef.current = setTimeout(() => {
      saveProgress();
    }, 5000); // Save after 5 seconds of no changes

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [workNotes, checklist, questionnaire]);

  // Save to localStorage for quick resume
  useEffect(() => {
    if (!instanceId || !clockedIn) return;

    const draft = {
      workNotes,
      checklist,
      questionnaire,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${instanceId}`, JSON.stringify(draft));
  }, [workNotes, checklist, questionnaire, instanceId, clockedIn]);

  const loadJobInstance = async () => {
    try {
      setLoading(true);
      setError('');

      const data = await contractorJobService.getJobInstanceDetails(instanceId);
      setJobInstance(data);

      // Check if this is a resubmission (needs_revision or disputed)
      if (data.status === 'needs_revision' || data.status === 'disputed') {
        setIsResubmission(true);
        // Parse feedback from notes field
        const notes = data.job?.notes || data.notes;
        if (notes) {
          const match = notes.match(/^\[([A-Z_]+)\]\s*(.*)$/);
          if (match) {
            setRevisionFeedback({
              category: match[1].toLowerCase().replace('_', ' '),
              reason: match[2],
              type: data.status
            });
          } else {
            setRevisionFeedback({
              category: null,
              reason: notes,
              type: data.status
            });
          }
        }
        // For resubmissions, user is already "clocked in" - they're editing existing data
        setClockedIn(true);
        if (data.clockInTime) {
          setClockInTime(new Date(data.clockInTime));
          // Pre-fill editable time fields for resubmission
          setEditClockIn(new Date(data.clockInTime).toISOString().slice(0, 16));
        }
        if (data.clockOutTime) {
          setEditClockOut(new Date(data.clockOutTime).toISOString().slice(0, 16));
        }
      }
      // Check if already clocked in (normal flow)
      else if (data.status === 'in_progress' && data.clockInTime) {
        setClockedIn(true);
        setClockInTime(new Date(data.clockInTime));
      }

      // Load saved work notes and checklist from database
      if (data.workNotes) setWorkNotes(data.workNotes);
      if (data.checklistData) setChecklist(data.checklistData);
      if (data.questionnaireResponses) {
        // Map saved responses to questionnaire format
        const template = data.job?.questionnaire_template || data.job?.questionnaireTemplate || [];
        const responses = data.questionnaireResponses;
        setQuestionnaire(template.map((q, idx) => ({
          id: idx + 1,
          question: q.question,
          type: q.type || 'text',
          required: q.required || false,
          options: q.options || [],
          answer: responses?.find(r => r.question === q.question)?.answer || ''
        })));
      }

      // Load job requirements
      const job = data.job;
      const jobRequirements = {
        photosRequiredBefore: job?.photos_required_before ?? job?.photosRequiredBefore ?? false,
        photosRequiredAfter: job?.photos_required_after ?? job?.photosRequiredAfter ?? false,
        questionnaireTemplate: job?.questionnaire_template ?? job?.questionnaireTemplate ?? [],
        clockInFlexibility: job?.clock_in_flexibility ?? job?.clockInFlexibility ?? false
      };
      setRequirements(jobRequirements);

      // Initialize questionnaire from template if not loaded from responses
      if (!data.questionnaireResponses && jobRequirements.questionnaireTemplate?.length > 0) {
        setQuestionnaire(jobRequirements.questionnaireTemplate.map((q, idx) => ({
          id: idx + 1,
          question: q.question,
          type: q.type || 'text',
          required: q.required || false,
          options: q.options || [],
          answer: ''
        })));
      }

      // Initialize default checklist if not defined
      if (!data.checklistData || data.checklistData.length === 0) {
        setChecklist([
          { id: 1, text: 'Arrived at location', completed: false },
          { id: 2, text: 'Reviewed job requirements', completed: false },
          { id: 3, text: 'Ready to begin work', completed: false }
        ]);
      }

      // Load photos and categorize
      if (data.photos?.length > 0) {
        const photosWithUrls = await Promise.all(
          data.photos.map(async (photo) => {
            const url = await contractorJobService.getPhotoUrl(photo.storage_path);
            return {
              id: photo.id,
              url,
              storagePath: photo.storage_path,
              photoType: photo.photo_type,
              uploadedAt: photo.uploaded_at
            };
          })
        );

        setBeforePhotos(photosWithUrls.filter(p => p.photoType === 'before'));
        setAfterPhotos(photosWithUrls.filter(p => p.photoType === 'after'));
        setNotesPhotos(photosWithUrls.filter(p => p.photoType === 'notes'));
      }

      // Check localStorage for any unsaved changes
      const storedDraft = localStorage.getItem(`${STORAGE_KEY_PREFIX}${instanceId}`);
      if (storedDraft) {
        try {
          const draft = JSON.parse(storedDraft);
          // Only apply localStorage draft if it's newer than DB data
          const draftDate = new Date(draft.savedAt);
          const dbDate = data.updated_at ? new Date(data.updated_at) : new Date(0);

          if (draftDate > dbDate) {
            if (draft.workNotes !== undefined) setWorkNotes(draft.workNotes);
            if (draft.checklist?.length > 0) setChecklist(draft.checklist);
            if (draft.questionnaire?.length > 0) setQuestionnaire(draft.questionnaire);
          }
        } catch (e) {
          console.warn('Failed to parse draft from localStorage:', e);
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const saveProgress = async () => {
    if (!clockedIn || saving) return;

    try {
      setSaving(true);
      const checklistData = checklist.map(item => ({
        text: item.text,
        completed: item.completed
      }));
      const questionnaireData = questionnaire.map(q => ({
        question: q.question,
        answer: q.answer
      }));

      await contractorJobService.saveProgress(instanceId, {
        workNotes,
        checklistData,
        questionnaireResponses: questionnaireData
      });

      lastSavedRef.current = new Date();
    } catch (err) {
      console.warn('Auto-save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.warn('Geolocation not supported');
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.warn('Geolocation error:', error?.message);
          resolve(null);
        },
        { timeout: 10000, enableHighAccuracy: false }
      );
    });
  };

  const handleClockIn = async () => {
    try {
      setSubmitting(true);
      const location = await getCurrentLocation();

      await contractorJobService.clockIn(
        instanceId,
        location?.latitude || null,
        location?.longitude || null
      );

      setClockedIn(true);
      setClockInTime(new Date());
    } catch (err) {
      setError(`Clock in failed: ${err?.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualClockIn = async () => {
    if (!manualClockIn) {
      setError('Please select a clock-in time');
      return;
    }

    const manualTime = new Date(manualClockIn);
    const now = new Date();

    // Validate manual time is not in the future
    if (manualTime > now) {
      setError('Clock-in time cannot be in the future');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const location = await getCurrentLocation();

      await contractorJobService.clockInManual(
        instanceId,
        location?.latitude || null,
        location?.longitude || null,
        manualTime.toISOString()
      );

      setClockedIn(true);
      setClockInTime(manualTime);
    } catch (err) {
      setError(`Manual clock in failed: ${err?.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const validationErrors = [];

    if (requirements.photosRequiredBefore && beforePhotos.length === 0) {
      validationErrors.push('Before photos are required');
    }
    if (requirements.photosRequiredAfter && afterPhotos.length === 0) {
      validationErrors.push('After photos are required');
    }

    const unansweredRequired = questionnaire.filter(q => q.required && !q.answer?.trim());
    if (unansweredRequired.length > 0) {
      validationErrors.push(`Please answer all required questionnaire items`);
    }

    // Validate manual clock-out time if provided
    if (manualClockOut) {
      const manualOutTime = new Date(manualClockOut);
      const now = new Date();
      if (manualOutTime > now) {
        validationErrors.push('Clock-out time cannot be in the future');
      }
      if (clockInTime && manualOutTime < clockInTime) {
        validationErrors.push('Clock-out time must be after clock-in time');
      }
    }

    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const checklistData = checklist.map(item => ({
        text: item.text,
        completed: item.completed
      }));

      const questionnaireData = questionnaire.map(q => ({
        question: q.question,
        answer: q.answer
      }));

      // Handle resubmission (disputed/needs_revision) differently
      if (isResubmission) {
        // Validate edited times for resubmission
        if (editClockIn && editClockOut) {
          const editInTime = new Date(editClockIn);
          const editOutTime = new Date(editClockOut);
          const now = new Date();

          if (editInTime > now) {
            setError('Clock-in time cannot be in the future');
            return;
          }
          if (editOutTime > now) {
            setError('Clock-out time cannot be in the future');
            return;
          }
          if (editOutTime < editInTime) {
            setError('Clock-out time must be after clock-in time');
            return;
          }
        }

        const result = await contractorJobService.resubmitReport(
          instanceId,
          workNotes,
          checklistData,
          questionnaireData,
          editClockIn || null,
          editClockOut || null
        );

        // Clear localStorage draft
        localStorage.removeItem(`${STORAGE_KEY_PREFIX}${instanceId}`);

        // Check if overtime was detected after time changes
        if (result?.hasOvertime && result?.overtimeInfo) {
          setOvertimeInfo(result.overtimeInfo);
          setTimesheetId(result.timesheetId);
          setShowOvertimeModal(true);
        } else {
          // Navigate back to dashboard
          navigate('/contractor/dashboard', {
            state: { message: 'Report resubmitted successfully!' }
          });
        }
        return;
      }

      // Normal submission flow
      const location = await getCurrentLocation();

      // Prepare manual clock-out time if provided
      const manualClockOutTime = manualClockOut ? new Date(manualClockOut).toISOString() : null;

      const result = await contractorJobService.clockOut(
        instanceId,
        location?.latitude || null,
        location?.longitude || null,
        workNotes,
        checklistData,
        questionnaireData,
        null, // overtimeData
        manualClockOutTime
      );

      // Clear localStorage draft
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}${instanceId}`);

      // Check if overtime was detected
      if (result?.hasOvertime && result?.overtimeInfo) {
        setOvertimeInfo(result.overtimeInfo);
        setTimesheetId(result.timesheet?.id);
        setShowOvertimeModal(true);
      } else {
        // Navigate to success / dashboard
        navigate('/contractor/dashboard', {
          state: { message: 'Report submitted successfully!' }
        });
      }
    } catch (err) {
      setError(`Submit failed: ${err?.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOvertimeSubmit = async (overtimeData) => {
    try {
      setSubmitting(true);

      if (overtimeData?.requestOvertime && overtimeData?.justification && timesheetId) {
        await overtimeService.createOvertimeRequest({
          jobInstanceId: instanceId,
          timesheetId: timesheetId,
          contractorId: jobInstance?.assignedContractor,
          companyId: jobInstance?.job?.company_id,
          estimatedHours: overtimeInfo.estimatedHours,
          actualHours: overtimeInfo.actualHours,
          overtimeHours: overtimeInfo.overtimeHours,
          hourlyRate: overtimeInfo.hourlyRate,
          basePay: overtimeInfo.basePay,
          requestedOvertimeAmount: overtimeInfo.potentialOvertimePay,
          justification: overtimeData.justification
        });
      }

      setShowOvertimeModal(false);
      const successMessage = isResubmission
        ? 'Report resubmitted successfully!'
        : 'Report submitted successfully!';
      navigate('/contractor/dashboard', {
        state: { message: successMessage }
      });
    } catch (err) {
      setError(`Failed to submit overtime request: ${err?.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOvertimeSkip = () => {
    setShowOvertimeModal(false);
    const successMessage = isResubmission
      ? 'Report resubmitted successfully!'
      : 'Report submitted successfully!';
    navigate('/contractor/dashboard', {
      state: { message: successMessage }
    });
  };

  const handleClose = async () => {
    // Save progress before closing
    if (clockedIn) {
      await saveProgress();
    }
    onClose?.();
  };

  // Calculate summary stats
  const checklistComplete = checklist.filter(i => i.completed).length;
  const questionnaireComplete = questionnaire.filter(q => q.answer?.trim()).length;

  // Calculate validation state for submit button
  const getValidationErrors = useCallback(() => {
    const errors = [];

    if (requirements.photosRequiredBefore && beforePhotos.length === 0) {
      errors.push('Before photos required');
    }
    if (requirements.photosRequiredAfter && afterPhotos.length === 0) {
      errors.push('After photos required');
    }

    const unansweredRequired = questionnaire.filter(q => q.required && !q.answer?.trim());
    if (unansweredRequired.length > 0) {
      errors.push(`${unansweredRequired.length} required question(s) unanswered`);
    }

    return errors;
  }, [requirements, beforePhotos, afterPhotos, questionnaire]);

  const validationErrors = getValidationErrors();
  const canSubmit = validationErrors.length === 0;

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
        <div className="bg-background rounded-xl p-8 flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
          <p className="text-text-primary">Loading job details...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Dialog Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/70" onClick={handleClose} />

      {/* Dialog Container */}
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pointer-events-none">
        <div
          className={cn(
            'pointer-events-auto w-full bg-background',
            'sm:max-w-2xl sm:rounded-xl sm:max-h-[90vh]',
            'max-h-[95vh] rounded-t-2xl',
            'flex flex-col overflow-hidden'
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - Sticky */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-border bg-background">
            <div className="flex-1 min-w-0 pr-4">
              <h2 className="text-lg font-bold text-text-primary truncate">
                {jobInstance?.job?.job_name || 'Job Report'}
              </h2>
              <p className="text-sm text-text-secondary">
                {isResubmission
                  ? (revisionFeedback?.type === 'disputed' ? 'Under Dispute' : 'Needs Revision')
                  : (clockedIn ? 'In Progress' : 'Ready to start')}
                {saving && <span className="ml-2 text-primary">Saving...</span>}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface rounded-lg transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* Resubmission Feedback Banner */}
            {isResubmission && revisionFeedback && (
              <div className={cn(
                'p-4 rounded-xl border',
                revisionFeedback.type === 'disputed'
                  ? 'bg-orange-500/10 border-orange-500/30'
                  : 'bg-yellow-500/10 border-yellow-500/30'
              )}>
                <div className="flex items-start gap-3">
                  <MessageSquare className={cn(
                    'w-5 h-5 flex-shrink-0 mt-0.5',
                    revisionFeedback.type === 'disputed' ? 'text-orange-400' : 'text-yellow-400'
                  )} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className={cn(
                        'font-medium',
                        revisionFeedback.type === 'disputed' ? 'text-orange-400' : 'text-yellow-400'
                      )}>
                        {revisionFeedback.type === 'disputed' ? 'Report Under Dispute' : 'Revision Required'}
                      </h4>
                      {revisionFeedback.category && (
                        <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded text-xs">
                          {revisionFeedback.category}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text-primary mb-2">{revisionFeedback.reason}</p>
                    <p className="text-xs text-text-secondary">
                      Please review and address the feedback above, then resubmit your report.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Job Overview - Collapsible */}
            <div className="bg-surface border border-border rounded-xl overflow-hidden">
              <button
                onClick={() => setShowOverview(!showOverview)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-surface-light transition-colors"
              >
                <span className="font-medium text-text-primary">Job Details</span>
                {showOverview ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary" />
                )}
              </button>
              {showOverview && (
                <div className="px-4 pb-4 space-y-3 border-t border-border">
                  <div className="flex items-start gap-3 pt-3">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-text-primary">Location</div>
                      <div className="text-sm text-text-secondary">
                        {jobInstance?.job?.job_site?.address || jobInstance?.job?.location_address || 'No address provided'}
                      </div>
                    </div>
                  </div>
                  {/* Site Contact Person (from job_site) */}
                  {(jobInstance?.job?.job_site?.contact_person || jobInstance?.job?.job_site?.contact_phone) && (
                    <div className="flex items-start gap-3">
                      <User className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-text-primary">Site Contact</div>
                        <div className="text-sm text-text-secondary">
                          {jobInstance?.job?.job_site?.contact_person}
                          {jobInstance?.job?.job_site?.contact_phone && (
                            <span className="ml-2">• {jobInstance?.job?.job_site?.contact_phone}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-text-primary">Schedule</div>
                      <div className="text-sm text-text-secondary">
                        {jobInstance?.scheduledDate
                          ? new Date(jobInstance.scheduledDate).toLocaleDateString()
                          : 'Not scheduled'
                        }
                        {jobInstance?.scheduledTime && ` at ${jobInstance.scheduledTime}`}
                      </div>
                    </div>
                  </div>
                  {jobInstance?.job?.description && (
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-text-primary">Instructions</div>
                        <div className="text-sm text-text-secondary">
                          {jobInstance.job.description}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Clock In Section - Hidden for resubmissions */}
            {!isResubmission && (
            <div className="bg-surface border border-border rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center',
                  clockedIn ? 'bg-green-500/20' : 'bg-primary/20'
                )}>
                  {clockedIn ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <Clock className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-text-primary">
                    {clockedIn ? 'Clocked In' : 'Clock In'}
                  </h3>
                  {clockedIn && clockInTime && (
                    <p className="text-sm text-text-secondary">
                      Started at {clockInTime.toLocaleTimeString()}
                    </p>
                  )}
                </div>
              </div>

              {!clockedIn && (
                <>
                  {/* Standard clock-in */}
                  {!requirements.clockInFlexibility && (
                    <>
                      <p className="text-sm text-text-secondary mb-4">
                        Clock in to start recording your work. Your GPS location will be captured.
                      </p>
                      <button
                        onClick={handleClockIn}
                        disabled={submitting}
                        className={cn(
                          'w-full py-3 rounded-lg font-medium text-white transition-colors',
                          'bg-green-500 hover:bg-green-600',
                          'disabled:opacity-50 disabled:cursor-not-allowed',
                          'flex items-center justify-center gap-2'
                        )}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Getting Location...
                          </>
                        ) : (
                          <>
                            <Clock className="w-5 h-5" />
                            Clock In Now
                          </>
                        )}
                      </button>
                    </>
                  )}

                  {/* Late clock-in with manual time entry */}
                  {requirements.clockInFlexibility && (
                    <>
                      <div className="p-3 mb-4 bg-primary/10 border border-primary/20 rounded-lg">
                        <p className="text-sm text-primary font-medium">Late submission enabled</p>
                        <p className="text-xs text-text-secondary mt-1">
                          You can clock in now or enter your actual start time manually.
                        </p>
                      </div>

                      {/* Clock In Now button */}
                      <button
                        onClick={handleClockIn}
                        disabled={submitting}
                        className={cn(
                          'w-full py-3 rounded-lg font-medium text-white transition-colors mb-4',
                          'bg-green-500 hover:bg-green-600',
                          'disabled:opacity-50 disabled:cursor-not-allowed',
                          'flex items-center justify-center gap-2'
                        )}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Clock className="w-5 h-5" />
                            Clock In Now
                          </>
                        )}
                      </button>

                      {/* Divider */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex-1 h-px bg-border" />
                        <span className="text-xs text-text-tertiary">OR</span>
                        <div className="flex-1 h-px bg-border" />
                      </div>

                      {/* Manual time entry */}
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-text-primary mb-1">
                            Enter Manual Clock In Time
                          </label>
                          <input
                            type="datetime-local"
                            value={manualClockIn}
                            onChange={(e) => setManualClockIn(e.target.value)}
                            className={cn(
                              'w-full px-4 py-3 rounded-lg',
                              'bg-background border border-border',
                              'text-text-primary',
                              'focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary'
                            )}
                          />
                        </div>
                        <button
                          onClick={handleManualClockIn}
                          disabled={submitting || !manualClockIn}
                          className={cn(
                            'w-full py-3 rounded-lg font-medium text-white transition-colors',
                            manualClockIn
                              ? 'bg-primary hover:bg-primary-600'
                              : 'bg-gray-500 cursor-not-allowed',
                            'disabled:opacity-50 disabled:cursor-not-allowed',
                            'flex items-center justify-center gap-2'
                          )}
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            'Use Manual Time'
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            )}

            {/* Only show remaining sections if clocked in */}
            {clockedIn && (
              <>
                {/* Checklist Section */}
                <div className="bg-surface border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-text-primary">Checklist</h3>
                    <span className="text-sm text-text-secondary">
                      {checklistComplete}/{checklist.length} complete
                    </span>
                  </div>
                  <div className="space-y-2">
                    {checklist.map((item, index) => (
                      <label
                        key={item.id ?? index}
                        className={cn(
                          'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors',
                          'hover:bg-surface-light active:bg-surface-dark',
                          item.completed && 'bg-green-500/5'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={item.completed}
                          onChange={(e) => {
                            setChecklist(checklist.map(i =>
                              i.id === item.id ? { ...i, completed: e.target.checked } : i
                            ));
                          }}
                          className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
                        />
                        <span className={cn(
                          'text-text-primary',
                          item.completed && 'line-through text-text-secondary'
                        )}>
                          {item.text}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Before Photos Section */}
                <div className="bg-surface border border-border rounded-xl p-4">
                  <PhotoUploader
                    instanceId={instanceId}
                    photoType="before"
                    photos={beforePhotos}
                    onPhotosChange={setBeforePhotos}
                    required={requirements.photosRequiredBefore}
                    title="Before Photos"
                    description="Take photos of the work area before starting"
                  />
                </div>

                {/* Work Notes & Issues Section */}
                <div className="bg-surface border border-border rounded-xl p-4 space-y-4">
                  <div>
                    <h3 className="font-medium text-text-primary mb-1">Work Notes & Issues</h3>
                    <p className="text-sm text-text-secondary mb-3">
                      Add any notes or report issues found (e.g., broken tiles, cracked windows)
                    </p>
                    <textarea
                      value={workNotes}
                      onChange={(e) => setWorkNotes(e.target.value)}
                      placeholder="Enter your notes here... Report any issues like damage found on arrival, equipment problems, etc."
                      rows={4}
                      className={cn(
                        'w-full px-4 py-3 rounded-lg resize-none',
                        'bg-background border border-border',
                        'text-text-primary placeholder-text-tertiary',
                        'focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary'
                      )}
                    />
                  </div>

                  <PhotoUploader
                    instanceId={instanceId}
                    photoType="notes"
                    photos={notesPhotos}
                    onPhotosChange={setNotesPhotos}
                    required={false}
                    title="Issue Photos"
                    description="Upload photos documenting any issues"
                  />
                </div>

                {/* After Photos Section */}
                <div className="bg-surface border border-border rounded-xl p-4">
                  <PhotoUploader
                    instanceId={instanceId}
                    photoType="after"
                    photos={afterPhotos}
                    onPhotosChange={setAfterPhotos}
                    required={requirements.photosRequiredAfter}
                    title="After Photos"
                    description="Take photos of the completed work"
                  />
                </div>

                {/* Questionnaire Section */}
                {questionnaire.length > 0 && (
                  <div className="bg-surface border border-border rounded-xl p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium text-text-primary">Questionnaire</h3>
                      <span className="text-sm text-text-secondary">
                        {questionnaireComplete}/{questionnaire.length} answered
                      </span>
                    </div>
                    <div className="space-y-4">
                      {questionnaire.map((q) => (
                        <div key={q.id}>
                          <label className="block text-sm font-medium text-text-primary mb-2">
                            {q.question}
                            {q.required && (
                              <span className="ml-2 text-red-400 text-xs">*Required</span>
                            )}
                          </label>

                          {/* Text Input */}
                          {(!q.type || q.type === 'text') && (
                            <textarea
                              value={q.answer}
                              onChange={(e) => {
                                setQuestionnaire(questionnaire.map(item =>
                                  item.id === q.id ? { ...item, answer: e.target.value } : item
                                ));
                              }}
                              rows={2}
                              className={cn(
                                'w-full px-4 py-3 rounded-lg resize-none',
                                'bg-background border',
                                q.required && !q.answer?.trim() ? 'border-red-500/50' : 'border-border',
                                'text-text-primary placeholder-text-tertiary',
                                'focus:outline-none focus:border-primary'
                              )}
                            />
                          )}

                          {/* Yes/No */}
                          {q.type === 'yes_no' && (
                            <div className="flex gap-3">
                              {['Yes', 'No'].map((option) => (
                                <label
                                  key={option}
                                  className={cn(
                                    'flex-1 p-3 rounded-lg border cursor-pointer transition-all text-center',
                                    q.answer === option
                                      ? option === 'Yes'
                                        ? 'border-green-500 bg-green-500/10 text-green-400'
                                        : 'border-red-500 bg-red-500/10 text-red-400'
                                      : 'border-border hover:border-text-secondary text-text-primary'
                                  )}
                                >
                                  <input
                                    type="radio"
                                    name={`q-${q.id}`}
                                    value={option}
                                    checked={q.answer === option}
                                    onChange={(e) => {
                                      setQuestionnaire(questionnaire.map(item =>
                                        item.id === q.id ? { ...item, answer: e.target.value } : item
                                      ));
                                    }}
                                    className="sr-only"
                                  />
                                  {option}
                                </label>
                              ))}
                            </div>
                          )}

                          {/* Multiple Choice */}
                          {q.type === 'multiple_choice' && q.options?.length > 0 && (
                            <div className="space-y-2">
                              {q.options.map((option, optIdx) => (
                                <label
                                  key={optIdx}
                                  className={cn(
                                    'block p-3 rounded-lg border cursor-pointer transition-all',
                                    q.answer === option
                                      ? 'border-primary bg-primary/10 text-text-primary'
                                      : 'border-border hover:border-text-secondary text-text-primary'
                                  )}
                                >
                                  <input
                                    type="radio"
                                    name={`q-${q.id}`}
                                    value={option}
                                    checked={q.answer === option}
                                    onChange={(e) => {
                                      setQuestionnaire(questionnaire.map(item =>
                                        item.id === q.id ? { ...item, answer: e.target.value } : item
                                      ));
                                    }}
                                    className="sr-only"
                                  />
                                  {option}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual Clock Out Time Section (for late submissions) - Hidden for resubmissions */}
                {requirements.clockInFlexibility && !isResubmission && (
                  <div className="bg-surface border border-border rounded-xl p-4">
                    <h3 className="font-medium text-text-primary mb-1">Clock Out Time</h3>
                    <p className="text-sm text-text-secondary mb-3">
                      Enter your actual finish time, or leave blank to use current time when submitting
                    </p>
                    <input
                      type="datetime-local"
                      value={manualClockOut}
                      onChange={(e) => setManualClockOut(e.target.value)}
                      className={cn(
                        'w-full px-4 py-3 rounded-lg',
                        'bg-background border border-border',
                        'text-text-primary',
                        'focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary'
                      )}
                    />
                    {manualClockOut && clockInTime && new Date(manualClockOut) < clockInTime && (
                      <p className="text-sm text-red-400 mt-2">
                        Clock-out time must be after clock-in time
                      </p>
                    )}
                  </div>
                )}

                {/* Edit Clock In/Out Times Section (for resubmissions only) */}
                {isResubmission && (
                  <div className="bg-surface border border-border rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="font-medium text-text-primary">Edit Work Times</h3>
                        <p className="text-sm text-text-secondary">Adjust your clock in and out times if needed</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-1">
                          Clock In Time
                        </label>
                        <input
                          type="datetime-local"
                          value={editClockIn}
                          onChange={(e) => setEditClockIn(e.target.value)}
                          className={cn(
                            'w-full px-4 py-3 rounded-lg',
                            'bg-background border border-border',
                            'text-text-primary',
                            'focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary'
                          )}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-1">
                          Clock Out Time
                        </label>
                        <input
                          type="datetime-local"
                          value={editClockOut}
                          onChange={(e) => setEditClockOut(e.target.value)}
                          className={cn(
                            'w-full px-4 py-3 rounded-lg',
                            'bg-background border border-border',
                            'text-text-primary',
                            'focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary'
                          )}
                        />
                      </div>
                      {/* Validation warning */}
                      {editClockIn && editClockOut && new Date(editClockOut) < new Date(editClockIn) && (
                        <p className="text-sm text-red-400">
                          Clock-out time must be after clock-in time
                        </p>
                      )}
                      {(editClockIn && new Date(editClockIn) > new Date()) && (
                        <p className="text-sm text-red-400">
                          Clock-in time cannot be in the future
                        </p>
                      )}
                      {(editClockOut && new Date(editClockOut) > new Date()) && (
                        <p className="text-sm text-red-400">
                          Clock-out time cannot be in the future
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Summary Card */}
                <div className="bg-surface border border-border rounded-xl p-4">
                  <h3 className="font-medium text-text-primary mb-3">Report Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-text-secondary">Checklist</span>
                      <span className={cn(
                        'font-medium',
                        checklistComplete === checklist.length ? 'text-green-400' : 'text-text-primary'
                      )}>
                        {checklistComplete}/{checklist.length} complete
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-text-secondary">Before Photos</span>
                      <span className={cn(
                        'font-medium',
                        requirements.photosRequiredBefore && beforePhotos.length === 0
                          ? 'text-red-400'
                          : 'text-text-primary'
                      )}>
                        {beforePhotos.length} uploaded
                        {requirements.photosRequiredBefore && beforePhotos.length === 0 && ' (required)'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-text-secondary">After Photos</span>
                      <span className={cn(
                        'font-medium',
                        requirements.photosRequiredAfter && afterPhotos.length === 0
                          ? 'text-red-400'
                          : 'text-text-primary'
                      )}>
                        {afterPhotos.length} uploaded
                        {requirements.photosRequiredAfter && afterPhotos.length === 0 && ' (required)'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-text-secondary">Work Notes</span>
                      <span className="font-medium text-text-primary">
                        {workNotes?.trim() ? 'Added' : 'None'}
                      </span>
                    </div>
                    {notesPhotos.length > 0 && (
                      <div className="flex justify-between py-2 border-b border-border">
                        <span className="text-text-secondary">Issue Photos</span>
                        <span className="font-medium text-text-primary">
                          {notesPhotos.length} uploaded
                        </span>
                      </div>
                    )}
                    {questionnaire.length > 0 && (
                      <div className="flex justify-between py-2">
                        <span className="text-text-secondary">Questionnaire</span>
                        <span className={cn(
                          'font-medium',
                          questionnaire.some(q => q.required && !q.answer?.trim())
                            ? 'text-red-400'
                            : 'text-text-primary'
                        )}>
                          {questionnaireComplete}/{questionnaire.length} answered
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer - Sticky */}
          {clockedIn && (
            <div className="flex-shrink-0 px-4 py-3 border-t border-border bg-background safe-area-bottom">
              {/* Validation warnings */}
              {!canSubmit && validationErrors.length > 0 && (
                <div className="mb-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-sm text-red-400 font-medium mb-1">Cannot submit yet:</p>
                  <ul className="text-sm text-red-400 list-disc list-inside">
                    {validationErrors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                onClick={handleSubmit}
                disabled={submitting || !canSubmit}
                className={cn(
                  'w-full py-4 rounded-xl font-medium text-white transition-colors',
                  canSubmit
                    ? 'bg-primary hover:bg-primary-600'
                    : 'bg-gray-500 cursor-not-allowed',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'flex items-center justify-center gap-2'
                )}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {isResubmission ? 'Resubmitting...' : 'Submitting...'}
                  </>
                ) : (
                  <>
                    {isResubmission ? (
                      <>
                        <RefreshCw className="w-5 h-5" />
                        Resubmit Report
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Clock Out & Submit Report
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Overtime Modal */}
      {showOvertimeModal && (
        <OvertimeRequestModal
          overtimeInfo={overtimeInfo}
          onSubmit={handleOvertimeSubmit}
          onSkip={handleOvertimeSkip}
          onClose={handleOvertimeSkip}
          submitting={submitting}
        />
      )}
    </>
  );
}
